import React from "react";
import Modal from "react-responsive-modal";
import { Tooltip } from "react-tippy";
import * as Icon from "react-feather";

import "react-tippy/dist/tippy.css";

import TextAreaField from "../../common/text_area_field/TextAreaField";
import SearchableDropdown from "../../common/searchable_dropdown/SearchableDropdown";
import Checkbox from "../../common/checkbox/Checkbox";
import NoteService from "../../../services/NoteService";
import EngagementService from "../../../services/EngagementService";
import WComponent from "../../common/WComponent";
import Button from "../../common/button/Button";
import Loader from "../../common/loader/Loader";
import UtilityService from "../../../services/UtilityService";
import Linkify from "linkify-react";

import "./note-modal.css";
import clsx from "clsx";

const { alert } = UtilityService;

class NoteModal extends WComponent {
	constructor() {
		super();
		this.state = {
			noteId: "new",
			content: "",

			loading: false,
			show: false,
			isEditing: false,
			type: "general",

			notifyUsers: [],
			queueForNextRecurring: false
		};
	}

	componentDidMount() {
		this.resetComponent();
	}

	componentDidUpdate(prevProps) {
		let { noteId, type } = this.props;

		if (prevProps.noteId !== noteId || prevProps.type !== type) {
			this.resetComponent();
		}
	}

	async resetComponent() {
		let { noteId, type } = this.props;

		await this.update({
			type,
			loading: true,
			noteId,
			content: "",
			isEditing: noteId === "new", // If it's a new note, start in edit mode
			notifyUsers: [],
			queueForNextRecurring: false
		});

		if (noteId !== "new") {
			await this.fetchNote();
		}

		await this.update({
			loading: false
		});
	}

	fetchNote = async () => {
		let { noteId } = this.props;

		let note = await NoteService.fetchNote({ noteId });

		await this.update({ noteId: note.id, content: note.content });
	};

	show() {
		this.update({
			show: true
		});
	}

	updateNote = async () => {
		let { noteId, content, type } = this.state;

		alert({ type: "info", text: "Updating ..." });

		this.update({
			loading: true
		});

		let success = await NoteService.updateNote({ noteId, content, type });

		if (!success) {
			alert({ type: "error", text: `Failed to update ${UtilityService.getNoteType({ capitalizeFirstLetter: false, type })}.` });
		} else {
			alert({ type: "success", text: `Updated ${UtilityService.getNoteType({ capitalizeFirstLetter: false, type })} successfully!` });
		}

		this.update({
			loading: false,
			isEditing: false
		});
	};

	isValid = () => {
		let { content } = this.state;

		return content.length > 0;
	};

	createNote = async () => {
		let { content, type, notifyUsers, queueForNextRecurring } = this.state;
		let { engagementId, clientId, shouldPin } = this.props;

		if (!this.isValid()) {
			alert({ type: "error", text: "Missing fields!" });
			return;
		}

		this.update({
			loading: true
		});

		if (queueForNextRecurring) {
			await this.queueNoteForNextRecurring();

			this.update({
				loading: false
			});

			return;
		}

		let notifyUserIds = notifyUsers.map(u => u.id);

		let success = await NoteService.createNote({ content, engagementId, clientId, isPinned: shouldPin, type, notifyUserIds });

		if (!success) {
			alert({ type: "info", text: `Failed to create ${UtilityService.getNoteType({ capitalizeFirstLetter: false, type })}.` });
		} else {
			alert({ type: "success", text: `Created ${UtilityService.getNoteType({ capitalizeFirstLetter: false, type })} successfully!` });
		}

		this.update({
			loading: false
		});
	};

	queueNoteForNextRecurring = async () => {
		let { content, type } = this.state;
		let { shouldPin, recurringEngagement, onRecurringEngagementUpdated } = this.props;

		let updatedRecurringEngagement = await EngagementService.queueRecurringEngagementItems({
			recurringEngagementId: recurringEngagement.id,
			notes: [{ content, isPinned: shouldPin, type }]
		});

		if (!updatedRecurringEngagement) {
			alert({ type: "error", text: `Failed to save the ${UtilityService.getNoteType({ capitalizeFirstLetter: false, type })} for the next recurring engagement.` });
			return;
		}

		alert({ type: "success", text: `${UtilityService.getNoteType({ capitalizeFirstLetter: true, type })} saved! It will be added to the next recurring engagement.` });

		if (onRecurringEngagementUpdated) {
			onRecurringEngagementUpdated(updatedRecurringEngagement);
		}
	};

	isNewNote = () => {
		return this.state.noteId === "new";
	};

	onDeleteTask = async () => {
		let { noteId, type } = this.state;

		this.update({
			loading: true
		});

		let success = await NoteService.updateNote({ noteId, status: "deleted" });

		if (!success) {
			alert({ type: "error", text: `Failed to delete ${UtilityService.getNoteType({ capitalizeFirstLetter: false, type })}.` });
		} else {
			alert({ type: "success", text: `Deleted ${UtilityService.getNoteType({ capitalizeFirstLetter: false, type })} successfully!` });
		}

		this.update({
			loading: false,
			show: false
		});

		if (this.props.onClose) {
			this.props.onClose();
		}
	};

	onChange = async e => {
		let target = e.target;
		let value = target.type === "checkbox" ? target.checked : target.value;
		await this.update({
			[target.name]: value
		});
	};

	onClose = async confirmed => {
		let { isNewNote, createNote, updateNote } = this;
		let { onClose } = this.props;

		if (confirmed) {
			if (isNewNote()) {
				await createNote();
			} else {
				await updateNote();
			}
		}

		await this.update({
			show: false
		});

		if (onClose) {
			onClose();
		}
	};

	toggleEditMode = () => {
		this.setState({ isEditing: true });
	};

	onNotifyUserRemove = (userId) => {
		let { notifyUsers } = this.state;

		this.update({ notifyUsers: notifyUsers.filter(u => u.id !== userId) });
	};
	
	onNotifyUserSelect = (user) => {
		let { notifyUsers } = this.state;

		// Prevent duplicates
		let alreadyAdded = notifyUsers.some(u => u.id === user.id);
		if (alreadyAdded || !user.id) {
			return;
		}

		this.update({ notifyUsers: [...notifyUsers, user] });
	};

	onNotifyUserRemoveClick = (e) => {
		const { userId } = e.currentTarget.dataset;
		const parsedUserId = Number(userId);

		this.onNotifyUserRemove(parsedUserId);
	};

	renderNotifyChip = (user) => {
		const { id, name } = user;

		return (
			<div className="note-modal__notify__chip" key={id}>
				<span>{name}</span>
				<Icon.X size={14} data-user-id={id} onClick={this.onNotifyUserRemoveClick} />
			</div>
		);
	};

	renderNotifySection() {
		const { notifyUsers, queueForNextRecurring } = this.state;

		if (!this.isNewNote() || queueForNextRecurring) {
			return null;
		}

		return (
			<div className="note-modal__notify">
				<div className="note-modal__notify__label">Notify Users</div>
				<SearchableDropdown
					type="users"
					onSelect={this.onNotifyUserSelect}
					clearOnSelect
					showAll
					placeholder="Search users..."
				/>
				{notifyUsers.length ? (
					<div className="note-modal__notify__chips">{notifyUsers.map(this.renderNotifyChip)}</div>
				) : null}
			</div>
		);
	}

	renderQueueForNextRecurring() {
		const { queueForNextRecurring, type } = this.state;
		const { recurringEngagement } = this.props;

		if (!this.isNewNote() || !recurringEngagement) {
			return null;
		}

		const noteType = UtilityService.getNoteType({ capitalizeFirstLetter: false, type });

		return (
			<div className="note-modal__queue-recurring">
				<Checkbox
					title="Save for the next recurring engagement"
					name="queueForNextRecurring"
					checked={queueForNextRecurring}
					onChange={this.onChange}
					className="ft-note-queue-recurring"
				/>
				<Tooltip
					arrow
					position="top"
					title={`This ${noteType} will not be added to the current engagement. It will be saved on the recurrence schedule and automatically added to the next engagement generated by it. Until then, you can view or remove it from the engagement's Repeat tab.`}
				>
					<Icon.Info className="note-modal__queue-recurring__info-icon" size={15} />
				</Tooltip>
			</div>
		);
	}

	render() {
		let { isNewNote } = this;
		let { show, content, loading, isEditing, type } = this.state;

		return (
			<Modal classNames={{ modal: "note-modal" }} open={show} onClose={() => this.onClose(false)} center>
				<div>
					<div className="note-modal__title">
						{isNewNote()
							? `New ${UtilityService.getNoteType({ capitalizeFirstLetter: true, type })}`
							: UtilityService.getNoteType({ capitalizeFirstLetter: true, type })}
					</div>

					<div className="note-modal__body">
						{loading && (
							<div className="container-loader">
								<Loader />
							</div>
						)}
						{!loading && isEditing ? (
							<TextAreaField className="note-modal__body__input" type="text" name="content" value={content} onChange={this.onChange} />
						) : type === "reference" ? (
							<Linkify as="p" onClick={this.toggleEditMode} style={{ whiteSpace: "pre-wrap" }}>
								{content}
							</Linkify>
						) : (
							<TextAreaField className="note-modal__body__input" type="text" name="content" value={content} onChange={this.onChange} />
						)}
					</div>

					{!loading && this.renderQueueForNextRecurring()}
					{!loading && this.renderNotifySection()}
				</div>
				<div className="note-modal__footer">
					{!isNewNote() && (
						<Button
							view="secondary"
							className={clsx("note-modal__footer__delete-btn", "ft-unb-e")}
							onClick={this.onDeleteTask}
							icon={<Icon.Trash2 size={22} />}
						></Button>
					)}

					<Button view="secondary" className="ft-unc-e" text="Cancel" onClick={() => this.onClose(false)} />

					<Button
						className="ft-unb-e"
						text={
							isNewNote()
								? `Create ${UtilityService.getNoteType({ capitalizeFirstLetter: true, type })}`
								: `Update ${UtilityService.getNoteType({ capitalizeFirstLetter: true })}`
						}
						onClick={() => this.onClose(true)}
					/>
				</div>
			</Modal>
		);
	}
}

export default NoteModal;
